import classNames from 'clsx';
import hotkeys from 'hotkeys-js';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_B2B_BIZ_USER, NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { createAlbumRequest, loadAlbumsListRequest } from 'reactApp/modules/albums/albums.actions';
import { CUSTOM_ALBUMS_ID } from 'reactApp/modules/albums/albums.constants';
import { getCustomAlbumsErrorState, getCustomAlbumsList, getCustomAlbumsLoadingState } from 'reactApp/modules/albums/albums.selector';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { disableDropArea, enableDropArea } from 'reactApp/modules/uploading/uploading.module';
import { Album } from 'reactApp/ui/Album/Album';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { CreateAlbum } from 'reactApp/ui/CreateAlbum/CreateAlbum';
import { Empty } from 'reactApp/ui/Empty/Empty';
import { ErrorRetry } from 'reactApp/ui/ErrorRetry/ErrorRetry';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { AlbumsEmpty } from './AlbumsEmpty/AlbumsEmpty';
import styles from './AlbumsPage.css';
import { AlbumsSection } from './AlbumsSection/AlbumsSection';

const HOT_KEY_SCOPE_NAME = 'albums_page';

export const AlbumsPage = memo(function AlbumsPage() {
    const dispatch = useDispatch();
    const customAlbums = useSelector(getCustomAlbumsList);
    const isEmptyAlbumsList = !customAlbums.length;
    const isLoading = useSelector(getCustomAlbumsLoadingState);
    const error = useSelector(getCustomAlbumsErrorState);

    const createAlbumHandler = useCallback(() => {
        dispatch(createAlbumRequest());
    }, []);

    useHotKeyScope(HOT_KEY_SCOPE_NAME);

    useEffect(() => {
        const handler = function (e) {
            e.preventDefault();

            createAlbumHandler();
        };

        hotkeys(`shift+a`, HOT_KEY_SCOPE_NAME, handler);

        return () => {
            hotkeys.unbind(`shift+a`, HOT_KEY_SCOPE_NAME, handler);
        };
    }, [createAlbumHandler]);

    useEffect(() => {
        dispatch(routeChangeSuccess({ id: '/', storage: EStorageType.albums, params: {}, __isFolder: true, url: '', __parent: '' }));
        dispatch(loadAlbumsListRequest());
    }, []);

    useEffect(() => {
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'albums' });
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: 'albums' });
    }, []);

    useEffect(() => {
        dispatch(disableDropArea());

        return () => {
            dispatch(enableDropArea());
        };
    }, []);

    const content = useMemo(() => {
        if (error) {
            return <ErrorRetry centered />;
        }

        if (isLoading) {
            return <Spinner />;
        }

        if (isEmptyAlbumsList) {
            return (
                <div className={styles.emptyContent} data-qa-id="albums-empty">
                    {/* TODO: CLOUDWEB-14876 - заменить на новый компонент empty-state'а после аб. */}
                    {IS_B2B_BIZ_USER ? <Empty.New storage={EStorageType.albums} categoryType={EStorageType.albums} /> : <AlbumsEmpty />}
                </div>
            );
        }

        return (
            <AlbumsSection title="Мои альбомы" sectionRouteId={CUSTOM_ALBUMS_ID}>
                <div className={styles.album}>
                    <CreateAlbum onClick={createAlbumHandler} />
                </div>
                {customAlbums.map((album) => (
                    <Album key={album.id} {...album} />
                ))}
            </AlbumsSection>
        );
    }, [error, isEmptyAlbumsList, isLoading, customAlbums]);

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
            })}
        >
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    <BreadcrumbsContainer />
                </div>

                {content}

                <Footer storage={EStorageType.albums} isAbsolutePos={true} />
            </div>
        </div>
    );
});
